import { FC } from 'react';

import { MapPin, Package, Truck } from 'lucide-react';

import { OfferPropertiesText } from '../../components/shared/OfferPropertiesText';
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from '../../components/ui/Card';
import { Separator } from '../../components/ui/Separator';
import { CartItem } from '../../context/cart-context';
import { OfferProperty } from '../../types';

interface DeliveryInfo {
    method: string;
    recipient_name: string;
    recipient_phone: string;
    city: string;
    region: string;
    delivery_point_type: string;
    delivery_point_number: string;
    post_office: string;
}

interface Props {
    totalPrice: number;
    items?: CartItem[];
    offerProperties?: OfferProperty[];
    delivery?: DeliveryInfo;
    message?: string;
}

const METHOD_LABELS: Record<string, { label: string; icon: typeof Truck }> = {
    nova_poshta: { label: 'Нова Пошта (Відділення)', icon: Truck },
    ukrposhta: { label: 'Укрпошта', icon: Package },
    pickup: { label: 'Самовивіз', icon: MapPin },
};

export const OrderSummary: FC<Props> = ({
    totalPrice,
    items,
    offerProperties = [],
    delivery,
    message,
}) => {
    const methodInfo = delivery ? METHOD_LABELS[delivery.method] : null;
    const Icon = methodInfo?.icon;

    const hasRecipient = delivery?.recipient_name || delivery?.recipient_phone;
    const hasAddress =
        delivery?.city ||
        delivery?.delivery_point_number ||
        delivery?.post_office;

    return (
        <Card>
            <CardHeader>
                <CardTitle>Підсумок замовлення</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {/* Cart items */}
                    {items && items.length > 0 && (
                        <>
                            <div className="space-y-2">
                                {items.map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-start justify-between gap-2 text-sm"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <span className="line-clamp-2 text-muted-foreground">
                                                {item.name}
                                                {item.quantity > 1 && (
                                                    <span className="ml-1 text-xs">
                                                        ×{item.quantity}
                                                    </span>
                                                )}
                                            </span>
                                            <OfferPropertiesText
                                                propertySet={item.propertySet}
                                                legacyCondition={item.condition}
                                                offerProperties={
                                                    offerProperties
                                                }
                                                productTypeId={
                                                    item.productTypeId
                                                }
                                                className="mt-0.5"
                                                dataTestId={`checkout-summary-offer-properties-${item.id}`}
                                            />
                                        </div>
                                        <span className="font-medium whitespace-nowrap">
                                            ₴
                                            {(
                                                item.price * item.quantity
                                            ).toFixed(2)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <Separator />
                        </>
                    )}

                    {/* Delivery info */}
                    {delivery && methodInfo && (
                        <>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    {Icon && (
                                        <Icon className="h-4 w-4 text-primary" />
                                    )}
                                    <span>{methodInfo.label}</span>
                                </div>

                                {hasRecipient && (
                                    <div className="space-y-0.5 text-sm text-muted-foreground">
                                        {delivery.recipient_name && (
                                            <p>{delivery.recipient_name}</p>
                                        )}
                                        {delivery.recipient_phone && (
                                            <p>{delivery.recipient_phone}</p>
                                        )}
                                    </div>
                                )}

                                {hasAddress && (
                                    <div className="space-y-0.5 text-sm text-muted-foreground">
                                        {delivery.city && (
                                            <p>
                                                {delivery.city}
                                                {delivery.region
                                                    ? `, ${delivery.region}`
                                                    : ''}
                                            </p>
                                        )}
                                        {delivery.method === 'nova_poshta' &&
                                            delivery.delivery_point_number && (
                                                <p>
                                                    {delivery.delivery_point_type ===
                                                    'parcel_locker'
                                                        ? 'Поштомат'
                                                        : 'Відділення'}{' '}
                                                    №
                                                    {
                                                        delivery.delivery_point_number
                                                    }
                                                </p>
                                            )}
                                        {delivery.method === 'ukrposhta' &&
                                            delivery.post_office && (
                                                <p>{delivery.post_office}</p>
                                            )}
                                    </div>
                                )}

                                {delivery.method === 'pickup' && (
                                    <p className="text-sm text-muted-foreground">
                                        Забрати особисто у продавця
                                    </p>
                                )}
                            </div>
                            <Separator />
                        </>
                    )}

                    <div className="flex justify-between text-lg font-bold">
                        <span>Загалом:</span>
                        <span>₴{totalPrice.toFixed(2)}</span>
                    </div>

                    {message && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                            <p className="text-sm font-normal text-red-800">
                                {message}
                            </p>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
